import { useEffect, useState } from "react";
import { Button, Input, Modal, Typography, message } from "antd";
import {
  deleteWorkspaceApi,
  getWorkspaceDeletePreviewApi,
} from "../../api/admin.workspaces.api";

const { Text, Paragraph } = Typography;

function UserLines({ rows, empty }) {
  if (!rows.length) {
    return <Paragraph type="secondary">{empty}</Paragraph>;
  }
  return (
    <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
      {rows.map((row) => (
        <li key={row.user_id}>
          {row.email || row.user_id}
          {row.role ? ` (${row.role})` : ""}
          {row.replacement_workspace_id
            ? ` — active and default move to ${row.replacement_workspace_name || row.replacement_workspace_id}`
            : ""}
        </li>
      ))}
    </ul>
  );
}

function WorkspaceDeleteModal({
  open,
  workspaceId,
  onCancel,
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => {
    if (!open || !workspaceId) {
      return undefined;
    }
    let cancelled = false;
    setConfirmName("");
    setPreview(null);
    setLoading(true);
    getWorkspaceDeletePreviewApi(workspaceId)
      .then((res) => {
        if (cancelled) return;
        if (res.data.status === "success") {
          setPreview(res.data.data);
        } else {
          message.error(res.data.message || "Failed to load delete preview");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          message.error(err.response?.data?.message || "Failed to load delete preview");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const expected = (preview?.workspace_name || "").trim();
  const nameMatches = confirmName.trim() === expected && expected.length > 0;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await deleteWorkspaceApi(workspaceId, { confirm_name: confirmName.trim() });
      if (res.data.status === "success") {
        message.success("Workspace deleted");
        onDeleted?.(res.data.data);
      } else {
        message.error(res.data.message || "Failed to delete workspace");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to delete workspace");
    } finally {
      setDeleting(false);
    }
  };

  const exclusive = preview?.exclusive_users || [];
  const kept = preview?.kept_users || [];

  return (
    <Modal
      title="Delete workspace"
      open={open}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="delete"
          danger
          type="primary"
          loading={deleting}
          disabled={!nameMatches || loading}
          onClick={handleDelete}
        >
          Delete workspace
        </Button>,
      ]}
    >
      <Paragraph>
        This permanently deletes the workspace and its properties, memberships,
        invitations, GA4 connection, and replay connection. Users who belong only
        to this workspace are deleted. Users who belong to other workspaces are kept.
      </Paragraph>
      {loading ? (
        <Paragraph>Loading who will be deleted…</Paragraph>
      ) : (
        <>
          <Text strong>Users deleted</Text>
          <UserLines rows={exclusive} empty="No users will be deleted." />
          <Text strong>Users kept</Text>
          <UserLines rows={kept} empty="No other members." />
        </>
      )}
      <Paragraph>
        Type <Text code>{preview?.workspace_name || ""}</Text> to confirm.
      </Paragraph>
      <Input
        value={confirmName}
        onChange={(e) => setConfirmName(e.target.value)}
        placeholder="Workspace name"
        autoComplete="off"
      />
    </Modal>
  );
}

export default WorkspaceDeleteModal;
